import React, { useEffect, useMemo, useState } from "react";
import { api } from "../scripts/api";
import { Upload, Save, RefreshCw, Trash2 } from "lucide-react";

function InputCell({ value, onChange, disabled }) {
  return (
    <input
      className="input"
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", minWidth: 140 }}
    />
  );
}

export default function AdminVendorsPage() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [file, setFile] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.get("/vendors", { params: { q, limit: 500 } });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setCount(Number(data?.count ?? 0));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({ ...r });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setLoading(true);
    setMsg("");
    try {
      const { data } = await api.put(`/vendors/${editingId}`, draft);
      setRows((prev) => prev.map((x) => (x.id === editingId ? data : x)));
      cancelEdit();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const delRow = async (id) => {
    if (!window.confirm(`Delete vendor #${id}?`)) return;
    setLoading(true);
    setMsg("");
    try {
      await api.delete(`/vendors/${id}`);
      setRows((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setMsg(e?.response?.data?.error || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vendors/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg(`Imported ${data?.imported ?? 0} vendors.`);
      setFile(null);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.error || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const visible = useMemo(() => rows, [rows]);

  return (
    <div className="container space-y">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Vendors</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            {count} records
          </div>
        </div>

        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="Search name/email/phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 320 }}
          />
          <button className="btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={16} style={{ marginRight: 8 }} />
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="muted">{msg}</div> : null}

      <div className="card">
        <div style={{ fontWeight: 900, marginBottom: 10 }}>CSV Import</div>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button className="btn" onClick={doImport} disabled={!file || loading}>
            <Upload size={16} style={{ marginRight: 8 }} />
            Import
          </button>
          <div className="muted" style={{ fontSize: 12 }}>
            Suggested headers: <code>name,email,phone,website,address,city,province,postal_code,country,notes</code>
          </div>
        </div>
      </div>

      <div className="card" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {["id", "name", "email", "phone", "website", "city", "province", "notes", "actions"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: 10,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                    opacity: 0.8,
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => {
              const editing = editingId === r.id;
              const row = editing ? draft : r;

              return (
                <tr key={r.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding: 10, whiteSpace: "nowrap" }}>#{r.id}</td>

                  <td style={{ padding: 10 }}>
                    <InputCell disabled={!editing} value={row.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell disabled={!editing} value={row.email} onChange={(v) => setDraft((d) => ({ ...d, email: v }))} />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell disabled={!editing} value={row.phone} onChange={(v) => setDraft((d) => ({ ...d, phone: v }))} />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell
                      disabled={!editing}
                      value={row.website}
                      onChange={(v) => setDraft((d) => ({ ...d, website: v }))}
                    />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell disabled={!editing} value={row.city} onChange={(v) => setDraft((d) => ({ ...d, city: v }))} />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell
                      disabled={!editing}
                      value={row.province}
                      onChange={(v) => setDraft((d) => ({ ...d, province: v }))}
                    />
                  </td>
                  <td style={{ padding: 10 }}>
                    <InputCell disabled={!editing} value={row.notes} onChange={(v) => setDraft((d) => ({ ...d, notes: v }))} />
                  </td>

                  <td style={{ padding: 10, whiteSpace: "nowrap" }}>
                    {!editing ? (
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn-outline" onClick={() => startEdit(r)} disabled={loading}>
                          Edit
                        </button>
                        <button className="btn-outline" onClick={() => delRow(r.id)} disabled={loading} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn" onClick={saveEdit} disabled={loading}>
                          <Save size={16} style={{ marginRight: 8 }} />
                          Save
                        </button>
                        <button className="btn-outline" onClick={cancelEdit} disabled={loading}>
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {visible.length === 0 ? (
              <tr>
                <td colSpan={9} className="muted" style={{ padding: 12 }}>
                  No vendors.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
